const STORAGE_KEY = 'trustichain.savingsPlanTypes';
const TARGET_DATE_STORAGE_KEY = 'trustichain.savingsTargetDates';

const PLAN_TYPE_LABELS = {
  fixed: 'Fixed',
  flex: 'Flex Savings',
  'flex savings': 'Flex Savings',
  auto: 'Auto Savings',
  'auto savings': 'Auto Savings',
  autosavings: 'Auto Savings',
  goal: 'Goal',
};

export function normalizeSavingsPlanType(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return PLAN_TYPE_LABELS[raw.toLowerCase()] || raw;
}

function readStoredPlanTypes() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function rememberSavingsPlanType(walletId, planType, walletName) {
  const label = normalizeSavingsPlanType(planType);
  if (!label) return;
  const next = { ...readStoredPlanTypes() };
  const id = String(walletId || '').trim();
  const name = String(walletName || '').trim().toLowerCase();
  if (id) next[id] = label;
  if (name) next[`name:${name}`] = label;
  if (!id && !name) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

export function resolveSavingsPlanType(wallet) {
  const fromApi = normalizeSavingsPlanType(
    wallet?.planType || wallet?.savingsType || wallet?.category || wallet?.plan || wallet?.type,
  );
  if (fromApi && fromApi.toLowerCase() !== 'savings') return fromApi;
  const stored = readStoredPlanTypes();
  const id = String(wallet?.id || '').trim();
  const byId = id ? normalizeSavingsPlanType(stored[id]) : '';
  if (byId) return byId;
  const name = String(wallet?.name || wallet?.title || '').trim().toLowerCase();
  const byName = name ? normalizeSavingsPlanType(stored[`name:${name}`]) : '';
  return byName || 'Savings';
}

export function toSavingsIsoDate(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const date = value instanceof Date ? value : new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function localTodayIsoDate() {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

export function formatSavingsIsoDate(iso) {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function readStoredTargetDates() {
  try {
    const parsed = JSON.parse(localStorage.getItem(TARGET_DATE_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function rememberSavingsTargetDate(walletId, targetDate, walletName) {
  const iso = toSavingsIsoDate(targetDate);
  if (!iso) return;
  const next = { ...readStoredTargetDates() };
  const id = String(walletId || '').trim();
  const name = String(walletName || '').trim().toLowerCase();
  if (id) next[id] = iso;
  if (name) next[`name:${name}`] = iso;
  if (!id && !name) return;
  localStorage.setItem(TARGET_DATE_STORAGE_KEY, JSON.stringify(next));
}

export function resolveSavingsTargetDate(wallet) {
  const fromApi = toSavingsIsoDate(
    wallet?.targetDate || wallet?.target_date || wallet?.goalDate || wallet?.maturityDate,
  );
  if (fromApi) return fromApi;
  const stored = readStoredTargetDates();
  const id = String(wallet?.id || '').trim();
  if (id && stored[id]) return toSavingsIsoDate(stored[id]);
  const name = String(wallet?.name || wallet?.title || '').trim().toLowerCase();
  if (name && stored[`name:${name}`]) return toSavingsIsoDate(stored[`name:${name}`]);
  return '';
}

/** Fixed savings stays locked until the target date. Withdrawal opens on that date. */
export function fixedSavingsWithdrawLock(planType, targetDate) {
  if (normalizeSavingsPlanType(planType) !== 'Fixed') return null;
  const iso = toSavingsIsoDate(targetDate);
  if (!iso || iso <= localTodayIsoDate()) return null;
  const label = formatSavingsIsoDate(iso);
  return {
    locked: true,
    availableOn: iso,
    label: label ? `Locked until ${label}` : 'Locked until the target date',
    message: label
      ? `Fixed savings stays locked until ${label}.`
      : 'Fixed savings stays locked until the target date.',
  };
}
